import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  LinkOff as LinkOffIcon,
  MailOutline as MailOutlineIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { userApi, invitationApi, FPUserAccess, FPInvitation } from '../../services/userApi'
import { poolwerxColors } from '../../theme/colors'
import AddFPUserDialog from './AddFPUserDialog'
import ConfirmDialog from '../../components/shared/dialogs/ConfirmDialog'
import ConfirmUserFpBody from '../../components/admin/shared/ConfirmUserFpBody'
import { ADMIN_TABLE_HEADER_SX, inlineActionIconSx } from '../../components/admin/adminTableStyles'
import { useNotify } from '../../components/shared/ToastProvider'

interface FranchisePartnerAccessTabProps {
  fpCode: string
  fpId: number
  fpName?: string
}

type UnifiedRow =
  | { kind: 'user'; sortKey: number; user: FPUserAccess }
  | { kind: 'invite'; sortKey: number; invite: FPInvitation; isExpired: boolean }

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const formatExpiry = (
  expiresAt: string | null,
  isActive: boolean,
): { text: string; color: string } => {
  if (!expiresAt) return { text: 'Never', color: 'text.secondary' }

  const now = new Date()
  const expiry = new Date(expiresAt)
  const diffMs = expiry.getTime() - now.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)
  const diffDays = Math.floor(Math.abs(diffHours) / 24)
  const absHours = Math.floor(Math.abs(diffHours))

  if (diffMs <= 0) {
    if (diffDays === 0) return { text: `${absHours}h ago`, color: 'text.disabled' }
    if (diffDays === 1) return { text: '1 day ago', color: 'text.disabled' }
    return { text: `${diffDays} days ago`, color: 'text.disabled' }
  }

  if (diffHours <= 24) {
    return {
      text: absHours <= 1 ? 'in < 1 hour' : `in ${absHours} hours`,
      color: 'warning.main',
    }
  }

  if (diffDays === 1) {
    return { text: 'in 1 day', color: isActive ? 'success.main' : 'text.secondary' }
  }
  return { text: `in ${diffDays} days`, color: isActive ? 'success.main' : 'text.secondary' }
}

const FranchisePartnerAccessTab: React.FC<FranchisePartnerAccessTabProps> = ({
  fpCode,
  fpId,
  fpName,
}) => {
  const [users, setUsers] = useState<FPUserAccess[]>([])
  const [invitations, setInvitations] = useState<FPInvitation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<FPUserAccess | null>(null)
  const [removeSubmitting, setRemoveSubmitting] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [deleteInviteTarget, setDeleteInviteTarget] = useState<FPInvitation | null>(null)
  const [deleteInviteSubmitting, setDeleteInviteSubmitting] = useState(false)
  const [deleteInviteError, setDeleteInviteError] = useState<string | null>(null)
  const { showSuccess, showError } = useNotify()

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await userApi.getFPUsers(fpId)
      setUsers(response.data.users)
      setInvitations(response.data.invitations)
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load access data')
    } finally {
      setLoading(false)
    }
  }, [fpId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleConfirmRemove = useCallback(
    async (fpIds: number[], deleteAccount: boolean) => {
      if (!removeTarget) return
      setRemoveSubmitting(true)
      setRemoveError(null)
      try {
        const res = await userApi.revokeFPAccessBulk(removeTarget.id, {
          fp_ids: fpIds,
          delete_account_if_orphaned: deleteAccount,
        })
        const who = removeTarget.name || removeTarget.email
        const { user_deleted, user_deactivated } = res.data
        showSuccess(
          user_deleted
            ? `${who} deleted`
            : user_deactivated
              ? `${who} removed and deactivated (no FPs left)`
              : 'Access removed',
        )
        setRemoveTarget(null)
        await loadData()
      } catch (err: any) {
        setRemoveError(
          err.response?.data?.detail || err.message || 'Failed to remove access',
        )
      } finally {
        setRemoveSubmitting(false)
      }
    },
    [removeTarget, loadData, showSuccess],
  )

  const handleConfirmDeleteInvite = useCallback(async () => {
    if (!deleteInviteTarget) return
    setDeleteInviteSubmitting(true)
    setDeleteInviteError(null)
    try {
      await invitationApi.deleteInvitation(deleteInviteTarget.id)
      showSuccess('Invitation deleted')
      setDeleteInviteTarget(null)
      await loadData()
    } catch (err: any) {
      setDeleteInviteError(
        err.response?.data?.detail || err.message || 'Failed to delete invitation',
      )
    } finally {
      setDeleteInviteSubmitting(false)
    }
  }, [deleteInviteTarget, loadData, showSuccess])

  const handleDeactivateInvitation = useCallback(
    async (invitationId: number) => {
      try {
        await invitationApi.revokeInvitation(invitationId)
        showSuccess('Invitation deactivated')
        await loadData()
      } catch (err: any) {
        setError(
          err.response?.data?.detail || err.message || 'Failed to deactivate invitation',
        )
      }
    },
    [loadData],
  )

  const handleRegenerateInvitation = useCallback(
    async (inv: FPInvitation) => {
      if (!inv.invited_email) return
      try {
        await invitationApi.revokeInvitation(inv.id)
        await invitationApi.createInvitation({ fp_code: fpCode, email: inv.invited_email })
        showSuccess('Invitation regenerated')
        await loadData()
      } catch (err: any) {
        setError(
          err.response?.data?.detail || err.message || 'Failed to regenerate invitation',
        )
      }
    },
    [fpCode, loadData],
  )

  const handleCopyUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      showSuccess('URL copied to clipboard')
    } catch {
      showError('Failed to copy URL')
    }
  }, [])

  const existingUserIds = useMemo(() => new Set(users.map((u) => u.id)), [users])
  const existingInvitedEmails = useMemo(
    () =>
      new Set(
        invitations
          .filter((i) => i.is_active && i.is_usable && i.invited_email)
          .map((i) => (i.invited_email || '').toLowerCase()),
      ),
    [invitations],
  )

  const rows: UnifiedRow[] = useMemo(() => {
    // Sort priority: active users (0) → inactive users (1) → active invites (2) → expired/inactive invites (3)
    const userRows: UnifiedRow[] = users.map((u) => ({
      kind: 'user',
      sortKey: u.is_active ? 0 : 1,
      user: u,
    }))
    const inviteRows: UnifiedRow[] = invitations.map((inv) => {
      const isExpired = inv.expires_at ? new Date(inv.expires_at) < new Date() : false
      const active = inv.is_active && !isExpired
      return {
        kind: 'invite',
        sortKey: active ? 2 : 3,
        invite: inv,
        isExpired,
      }
    })
    return [...userRows, ...inviteRows].sort((a, b) => a.sortKey - b.sortKey)
  }, [users, invitations])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ color: poolwerxColors.teal, fontWeight: 600, flexGrow: 1 }}
        >
          Users &amp; Invitations
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{
            bgcolor: poolwerxColors.teal,
            '&:hover': { bgcolor: poolwerxColors.teal, opacity: 0.9 },
          }}
        >
          Add FP User
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Type</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Name / Email</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Other FPs</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Status</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX}>Date</TableCell>
            <TableCell sx={ADMIN_TABLE_HEADER_SX} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                No users or invitations for this Franchise Partner
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) =>
              row.kind === 'user' ? (
                <UserRow
                  key={`user-${row.user.id}`}
                  user={row.user}
                  onRevoke={setRemoveTarget}
                />
              ) : (
                <InviteRow
                  key={`invite-${row.invite.id}`}
                  invite={row.invite}
                  isExpired={row.isExpired}
                  onCopyUrl={handleCopyUrl}
                  onDeactivate={handleDeactivateInvitation}
                  onRegenerate={handleRegenerateInvitation}
                  onDelete={setDeleteInviteTarget}
                />
              ),
            )
          )}
        </TableBody>
      </Table>

      <AddFPUserDialog
        open={dialogOpen}
        fpCode={fpCode}
        fpName={fpName}
        existingUserIds={existingUserIds}
        existingInvitedEmails={existingInvitedEmails}
        onClose={() => setDialogOpen(false)}
        onGranted={(msg) => {
          setDialogOpen(false)
          showSuccess(msg)
          loadData()
        }}
        onInvited={(msg) => {
          setDialogOpen(false)
          showSuccess(msg)
          loadData()
        }}
      />

      <RemoveFPUserDialog
        user={removeTarget}
        fpId={fpId}
        fpCode={fpCode}
        fpName={fpName}
        submitting={removeSubmitting}
        errorText={removeError}
        onCancel={() => {
          setRemoveTarget(null)
          setRemoveError(null)
        }}
        onConfirm={handleConfirmRemove}
      />

      <ConfirmDialog
        open={deleteInviteTarget !== null}
        onClose={() => {
          setDeleteInviteTarget(null)
          setDeleteInviteError(null)
        }}
        onConfirm={handleConfirmDeleteInvite}
        title="Delete invitation"
        subtitle={fpName || fpCode}
        size="md"
        body={
          deleteInviteTarget ? (
            <ConfirmUserFpBody
              subjectKind="invite"
              subjectLabel="Invitation"
              subjectPrimary={deleteInviteTarget.invited_email || 'this invitation'}
              fpLabel="Franchise Partner"
              fps={[{ name: fpName || fpCode, fpCode }]}
              notice={{
                severity: 'warning',
                content: 'Permanently deletes this invitation. This cannot be undone.',
              }}
            />
          ) : null
        }
        tone="danger"
        confirmLabel="Delete"
        pending={deleteInviteSubmitting}
        errorText={deleteInviteError}
      />
    </Box>
  )
}

/** A row-action icon button - tooltip + matching aria-label + tinted hover. */
const RowActionButton: React.FC<{
  title: string
  color: string
  onClick: () => void
  children: React.ReactNode
}> = ({ title, color, onClick, children }) => (
  <Tooltip title={title}>
    <IconButton size="small" aria-label={title} onClick={onClick} sx={inlineActionIconSx(color)}>
      {children}
    </IconButton>
  </Tooltip>
)

interface UserRowProps {
  user: FPUserAccess
  onRevoke: (user: FPUserAccess) => void
}

const UserRow: React.FC<UserRowProps> = ({ user, onRevoke }) => {
  const others = user.other_fp_accesses || []
  return (
    <TableRow>
      <TableCell>
        <Chip
          icon={<PersonIcon sx={{ fontSize: 14 }} />}
          label="User"
          size="small"
          sx={{
            bgcolor: poolwerxColors.teal,
            color: 'white',
            '& .MuiChip-icon': { color: 'white' },
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {user.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {user.email}
        </Typography>
      </TableCell>
      <TableCell>
        {others.length > 0 ? (
          <Tooltip
            arrow
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}
                >
                  Also has access to:
                </Typography>
                {others.map((o) => (
                  <Typography key={o.fp_code} variant="caption" sx={{ display: 'block' }}>
                    • {o.fp_code} - {o.franchise_name}
                  </Typography>
                ))}
              </Box>
            }
          >
            <Chip
              label={`+${others.length} FP${others.length === 1 ? '' : 's'}`}
              size="small"
              variant="outlined"
              sx={{ cursor: 'help' }}
            />
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            -
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={user.is_active ? 'Active' : 'Inactive'}
          size="small"
          color={user.is_active ? 'success' : 'default'}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">Granted {formatDate(user.granted_at)}</Typography>
        {user.granted_by && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            by {user.granted_by}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <RowActionButton
          title="Remove access"
          color={poolwerxColors.stockNegative}
          onClick={() => onRevoke(user)}
        >
          <DeleteIcon fontSize="small" />
        </RowActionButton>
      </TableCell>
    </TableRow>
  )
}

interface InviteRowProps {
  invite: FPInvitation
  isExpired: boolean
  onCopyUrl: (url: string) => void
  onDeactivate: (invitationId: number) => void
  onRegenerate: (inv: FPInvitation) => void
  onDelete: (inv: FPInvitation) => void
}

const InviteRow: React.FC<InviteRowProps> = ({
  invite,
  isExpired,
  onCopyUrl,
  onDeactivate,
  onRegenerate,
  onDelete,
}) => {
  const active = invite.is_active && !isExpired
  const expiry = formatExpiry(invite.expires_at, active)
  const statusLabel = active ? 'Pending' : isExpired ? 'Expired' : 'Inactive'
  const statusColor: 'warning' | 'default' = active ? 'warning' : 'default'

  return (
    <TableRow>
      <TableCell>
        <Chip
          icon={<MailOutlineIcon sx={{ fontSize: 14 }} />}
          label="Invite"
          size="small"
          variant="outlined"
          sx={{
            color: 'text.secondary',
            '& .MuiChip-icon': { color: 'text.secondary' },
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {invite.invited_email || '-'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Invited by {invite.created_by} on {formatDate(invite.created_at)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          -
        </Typography>
      </TableCell>
      <TableCell>
        <Chip label={statusLabel} size="small" color={statusColor} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: expiry.color }}>
          {active ? `Expires ${expiry.text}` : expiry.text}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {invite.used_count}
          {invite.max_uses !== null ? `/${invite.max_uses}` : ''} uses
        </Typography>
      </TableCell>
      <TableCell align="right">
        <RowActionButton
          title="Copy invitation URL"
          color={poolwerxColors.teal}
          onClick={() => onCopyUrl(invite.invitation_url)}
        >
          <ContentCopyIcon fontSize="small" />
        </RowActionButton>
        {active && (
          <RowActionButton
            title="Deactivate invitation"
            color={poolwerxColors.stockNegative}
            onClick={() => onDeactivate(invite.id)}
          >
            <LinkOffIcon fontSize="small" />
          </RowActionButton>
        )}
        {!invite.is_usable && invite.invited_email && (
          <RowActionButton
            title="Regenerate invitation"
            color={poolwerxColors.teal}
            onClick={() => onRegenerate(invite)}
          >
            <RefreshIcon fontSize="small" />
          </RowActionButton>
        )}
        {!active && (
          <RowActionButton
            title="Delete invitation"
            color={poolwerxColors.stockNegative}
            onClick={() => onDelete(invite)}
          >
            <DeleteIcon fontSize="small" />
          </RowActionButton>
        )}
      </TableCell>
    </TableRow>
  )
}

interface RemoveFPUserDialogProps {
  user: FPUserAccess | null
  fpId: number
  fpCode: string
  fpName?: string
  submitting: boolean
  errorText: string | null
  onCancel: () => void
  onConfirm: (fpIds: number[], deleteAccount: boolean) => void
}

const RemoveFPUserDialog: React.FC<RemoveFPUserDialogProps> = ({
  user,
  fpId,
  fpCode,
  fpName,
  submitting,
  errorText,
  onCancel,
  onConfirm,
}) => {
  const open = user !== null

  const allFps = useMemo(() => {
    if (!user) return [] as { fpId: number; name: string; fpCode: string }[]
    const current = { fpId, name: fpName || fpCode, fpCode }
    const others = (user.other_fp_accesses || []).map((o) => ({
      fpId: o.fp_id,
      name: o.franchise_name,
      fpCode: o.fp_code,
    }))
    return [current, ...others]
  }, [user, fpId, fpCode, fpName])

  // "Update access" model: `kept` holds the FPs the user keeps (ticked). The
  // current FP starts unticked (flagged Removing); everything else stays ticked.
  const [kept, setKept] = useState<Set<number>>(new Set())
  const [deleteAccount, setDeleteAccount] = useState(false)

  useEffect(() => {
    if (user) {
      setKept(new Set(allFps.filter((fp) => fp.fpId !== fpId).map((fp) => fp.fpId)))
      setDeleteAccount(false)
    }
  }, [user, fpId, allFps])

  const toggle = (id: number) => {
    setKept((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const removedFpIds = allFps.filter((fp) => !kept.has(fp.fpId)).map((fp) => fp.fpId)
  const removesAll = allFps.length > 0 && removedFpIds.length === allFps.length
  const who = user ? user.name || user.email : ''

  const body = user ? (
    <ConfirmUserFpBody
      subjectKind="user"
      subjectLabel="User"
      subjectPrimary={who}
      subjectSecondary={user.email}
      fpLabel="Franchise Partner access"
      selectable
      destructiveUnchecked
      disabled={submitting}
      fps={allFps.map((fp) => ({
        fpId: fp.fpId,
        name: fp.name,
        fpCode: fp.fpCode,
        selected: kept.has(fp.fpId),
        onToggle: () => toggle(fp.fpId),
      }))}
      notice={
        removesAll
          ? {
              severity: 'error',
              content: `Removing all access leaves ${who} with no Franchise Partners and no access to any data.`,
            }
          : undefined
      }
      footer={
        removesAll ? (
          <Box>
            <FormControlLabel
              sx={{ mx: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={deleteAccount}
                  onChange={(e) => setDeleteAccount(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={
                <Typography variant="body2">
                  Also permanently delete this user account
                </Typography>
              }
            />
            {!deleteAccount && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                If you don't delete the account it will be deactivated. They'll be reactivated
                automatically if granted FP access again.
              </Typography>
            )}
          </Box>
        ) : undefined
      }
    />
  ) : null

  return (
    <ConfirmDialog
      open={open}
      onClose={onCancel}
      onConfirm={() => onConfirm(removedFpIds, removesAll && deleteAccount)}
      title="Update access"
      subtitle={fpName || fpCode}
      body={body}
      tone="warning"
      size="md"
      confirmLabel={removesAll && deleteAccount ? 'Delete user' : 'Update access'}
      confirmDisabled={removedFpIds.length === 0}
      pending={submitting}
      errorText={errorText}
    />
  )
}

export default FranchisePartnerAccessTab
